import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useRecordPagination from '../../shared/useRecordPagination';
import CompanySelect from '../../shared/components/companySelect.jsx';
import { Checkbox, Layout, TextField, FormLayout, Form, Page, Card, Select, BlockStack, Banner, Text, Button, InlineStack, Box, InlineGrid } from '@shopify/polaris';
import { useUrl, handleSaveWithSecondPayload } from '../../shared/util/hanldesave';
import { alphabetize, getCurrenctDatetimeYYYMMDD, getExpiryDate, getRequest, putRequest } from '../../shared/components/functions.jsx';
import { useAuthStore, useLookupStore } from '../../context/useStore.js';
import DocumentCard from '../../shared/components/cards/DocumentCard';
import S3DocumentsCard from '../../shared/components/cards/S3DocumentsCard';
import { validateSchema } from '../../helpers/schemas/schemasHelpers.js';
import IncasserenSelect from '../../shared/components/incasserenSelect.jsx';
import Toasts from '../../shared/components/toasts/toasts.jsx';
import { TheSaveBar } from '../../shared/components/theSaveBar.jsx';
import { getPageTitle, TabTitle } from '../../shell/helmet.jsx';
import { getSchema } from '../../shared/formSchemas.js';
import EventTimeLine from '../../shared/components/events/eventTimeLine.jsx';
import InvoiceBankTransactions from '../../shared/components/invoiceBankTransactions.jsx';
import PaymentIntentsCard from '../../shared/components/banking/PaymentIntentsCard.jsx';
import InvoiceInCollection from '../invoices_in/components/invoiceInCollection.jsx';
import { PdfCard } from './components/PdfCard.jsx';
import ApprovalPanel from '../../shared/components/approvals/ApprovalPanel.jsx';
import { approvalsApi } from '../../shared/components/approvals/approvalsApi.js';
import OwnerSelect from '../../shared/components/ownerSelect.jsx';
import InvoiceLinesTable from './components/invoiceLinesTable';
import { CardHeading } from '../../shared/components/cards/CardHeading';
import userRoles from '../../utils/userRoles.js';
/*
  FIXME:
    - Not one invoice is assocciated with a project so we cant do any calculations on this for outgoing invoices, ingoing invoices dont even have yet the projectID property
    - not tracked how many vat is charged on these invoices?  hard to do revenue and profit calculations on those and compare it to the outgoing invoices
*/

export default function InvoiceInPage() {
  let { id } = useParams();
  const pagination = useRecordPagination('invoices_in', id, 'invoice IN');
  const navigate = useNavigate();
  let url = useUrl();
  const docTypeID = 19;
  const docType = 'invoice_in';
  const currencies = useLookupStore((state) => state.currencies);
  const paymentconditions = useLookupStore((state) => state.paymentconditions);
  const { user } = useAuthStore();
  const [initialInvoiceIn, setInitialInvoiceIn] = useState('');
  const [invoiceIn, setInvoiceIn] = useState('');
  const [toast, setToast] = useState(false);
  const [toastError, setToastError] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [company, setCompany] = useState({});
  const [approvalRefreshKey, setApprovalRefreshKey] = useState(0);
  const [formSubmitting, setFormSubmitting] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [selectedS3Key, setSelectedS3Key] = useState();
  const [hasSysDoc, setHasSysDoc] = useState(false);
  const [reopenLoading, setReopenLoading] = useState(false);
  const [invoiceLines, setInvoiceLines] = useState([]);
  const [supplierChangedByUser, setSupplierChangedByUser] = useState(false);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false); // same modal as the list: manual registration or a bank payment
  const [intentsRefreshToken, setIntentsRefreshToken] = useState(0);
  const pageTitle = getPageTitle(invoiceIn?.FactuurInNummer, 'Invoice IN');
  const invoiceInSchema = getSchema('invoice_in', url, null, user);
  const canDeleteProcessedInvoice = id !== 'new' && invoiceIn?.processed && (user?.role === userRoles.AdminFull || user?.role === userRoles.AdminData);

  const handleChange = (value, id) => {
    // console.log('id:', id, 'value:', value);
    let invoice = invoiceIn;
    if (id === 'status') invoice = { ...invoice, status: value, bedragopen: 0 };
    if (id === 'Bedragfaktuur') {
      invoice = { ...invoice, Bedragfaktuur: value, bedragopen: value };
      // Only Peppol invoices carry VAT; a manually entered total is already excl. VAT
      if (!invoice.PeppolJSON) invoice.BedragExclBtw = value;
    }
    setInvoiceIn({ ...invoice, [id]: value });
    setIsDirty(true);
  };

  const handleDiscard = () => {
    setInvoiceIn(initialInvoiceIn);
    setIsDirty(false);
    setFormSubmitting(false);
  };

  async function saveForm() {
    setFormSubmitting(true);
    if (invoiceIn.datum === '') invoiceIn.datum = null;
    if (invoiceIn.datumin === '') invoiceIn.datumin = null;
    if (invoiceIn.datumbetaling === '') invoiceIn.datumbetaling = null;
    if (invoiceIn.datumbetalingout === '') invoiceIn.datumbetalingout = null;
    if (typeof invoiceIn.bedragopen === 'string' && invoiceIn.bedragopen.includes(',')) invoiceIn.bedragopen = parseFloat(invoiceIn.bedragopen.replace(',', '.'));
    if (typeof invoiceIn.Bedragfaktuur === 'string' && invoiceIn.Bedragfaktuur.includes(',')) invoiceIn.Bedragfaktuur = parseFloat(invoiceIn.Bedragfaktuur.replace(',', '.'));
    if (typeof invoiceIn.BedragExclBtw === 'string' && invoiceIn.BedragExclBtw.includes(',')) invoiceIn.BedragExclBtw = parseFloat(invoiceIn.BedragExclBtw.replace(',', '.'));

    const errorInSchema = await validateSchema(invoiceInSchema, invoiceInSchema.cast(invoiceIn));

    if (!errorInSchema) {
      //check if combo exists
      const doubling = await getRequest(`/api/invoices_in?supplier=${company.NAAM}&reference=${invoiceIn.faktuurref}`);
      const filteredDoublings = doubling.filter((d) => d.faktuurinvolgnummer !== invoiceIn.faktuurinvolgnummer);
      if (
        filteredDoublings &&
        filteredDoublings.length > 0 &&
        !confirm(`One or more incoming invoices for this customer with the same reference already exists (${filteredDoublings.map((d) => d.FactuurInNummer).join(', ')}). Continue?`)
      ) {
        setIsDirty(false);
        return setFormSubmitting(false);
      }

      if (id !== 'new' && !invoiceIn.processed && invoiceIn.S3ObjectKeys) {
        // processing happens on the server: stamped PDF to SharePoint, Document row, accountant Dropbox, processed = 1 and fresh intake suggestions
        const processed = await approvalsApi.process('invoice', invoiceIn.faktuurinvolgnummer, { ...invoiceInSchema.cast(invoiceIn), selectedS3Key });
        if (!processed.ok) {
          setToastError(true);
          alert(`❌ Processing failed: ${processed.error}`);
          return setFormSubmitting(false);
        }
        setInvoiceIn(processed.data.document);
        setInitialInvoiceIn(processed.data.document);
        setIsDirty(false);
        setToast(true);
        setApprovalRefreshKey((key) => key + 1);
        return setFormSubmitting(false);
      }

      if (id === 'new') invoiceIn.processed = true;

      await handleSaveWithSecondPayload(
        id,
        'invoices_in',
        invoiceIn.faktuurinvolgnummer,
        'faktuurinvolgnummer',
        invoiceInSchema.cast(invoiceIn),
        { payload: [] },
        setInvoiceIn,
        setInitialInvoiceIn,
        `invoice-approvals/dump/lines/${id}`,
        () => {},
        () => {},
        isDirty,
        setIsDirty,
        false,
        () => {},
        setToast,
        setToastError,
        navigate
      );
    } else {
      console.log('Invoice In:', invoiceIn);
      alert(errorInSchema);
    }
    setFormSubmitting(false);
  }

  const getInvoiceLines = async () => {
    if (id !== 'new') {
      const data = await getRequest(`/api/invoice_lines/invoice/${id}`);
      setInvoiceLines(data || []);
    }
  };

  const fetchData = useCallback(async () => {
    //if url contains /new then set invoice in to its interface
    if (id === 'new') {
      setInvoiceIn(invoiceInSchema.default());
      setInitialInvoiceIn(invoiceInSchema.default());
    } else {
      //else get the invoice in resource via api get request
      const data = await getRequest(`/api/invoices_in/${id}`);
      if (data && data.length > 0) {
        const invoice = data[0];
        setInvoiceIn(invoice);
        setInitialInvoiceIn(invoice);

        if (invoice.LeverancierNummer) setCompany(await getRequest(`/api/companies/${invoice.LeverancierNummer}`));

        await getInvoiceLines();
      } else {
        setInvoiceIn(invoiceInSchema.default());
        setInitialInvoiceIn(invoiceInSchema.default());
      }
    }
    setDataLoaded(true);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (invoiceIn.PeppolStatus === null) updateExpiryDate();
  }, [invoiceIn.betalingID, invoiceIn.datum]);

  function updateExpiryDate() {
    setInvoiceIn((invoiceIn) => ({ ...invoiceIn, datumbetaling: getExpiryDate(invoiceIn.betalingID || undefined, invoiceIn?.datum || getCurrenctDatetimeYYYMMDD()) }));
  }

  useEffect(() => {
    async function fetchData() {
      if (invoiceIn.LeverancierNummer) {
        const data3 = await getRequest(`/api/companies/${invoiceIn.LeverancierNummer}`);
        setCompany(data3);

        if (!data3.Leverancier && !invoiceIn.datumbetaling) alert('This company is not marked as supplier, so not possible to set the correct payment conditions!');

        const invoiceCompanyData = { Leveranciernaam: data3.NAAM };
        if (!invoiceIn.processed && !hasSysDoc) invoiceCompanyData.betalingID = data3.LevBetalingID || undefined;

        setInvoiceIn((invoiceIn) => ({ ...invoiceIn, ...invoiceCompanyData }));

        // For Peppol invoices, check if the supplier's VAT number matches when user changes the supplier
        // This prompts the user to update the supplier's VAT when they manually select a supplier
        if (supplierChangedByUser && invoiceIn.PeppolStatus && invoiceIn.PeppolJSON) {
          setSupplierChangedByUser(false); // Reset the flag
          try {
            const peppolData = typeof invoiceIn.PeppolJSON === 'string' ? JSON.parse(invoiceIn.PeppolJSON) : invoiceIn.PeppolJSON;
            const peppolVatNumber = peppolData?.seller?.vatNumber;

            if (peppolVatNumber) {
              // Clean VAT numbers for comparison (remove spaces, dots, and make uppercase)
              const cleanPeppolVat = peppolVatNumber.replace(/[\s.]/g, '').toUpperCase();
              const cleanSupplierVat = (data3.BTW || '').replace(/[\s.]/g, '').toUpperCase();

              if (cleanPeppolVat !== cleanSupplierVat) {
                const updateVat = confirm(
                  `The VAT number of the selected supplier (${
                    data3.BTW || 'not set'
                  }) does not match the VAT number from the Peppol invoice (${peppolVatNumber}).\n\nWould you like to update the supplier's VAT number to "${peppolVatNumber}"?`
                );

                if (updateVat) {
                  try {
                    await putRequest(`/api/companies/${data3.FIRNR}`, { BTW: peppolVatNumber, BTWPlichtig: true }, setToast, setToastError);
                    setCompany((prev) => ({ ...prev, BTW: peppolVatNumber, BTWPlichtig: true }));
                  } catch (err) {
                    console.error('Failed to update supplier VAT number:', err);
                    alert('Failed to update supplier VAT number. Please try updating it manually.');
                  }
                }
              }
            }
          } catch (err) {
            console.error('Error parsing PeppolJSON:', err);
          }
        }
      }
    }
    fetchData();
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceIn.LeverancierNummer]);

  const handleDelete = async () => {
    const deleteMessage = invoiceIn?.processed
      ? 'Are you sure you want to delete this processed incoming invoice? This will also delete the connected invoice lines and approval/cost lines.'
      : 'Are you sure you want to delete this invoice?';

    if (invoiceIn?.processed && !canDeleteProcessedInvoice) {
      setToastError(true);
      return;
    }

    if (confirm(deleteMessage)) {
      setIsDirty(false);
      const result = await fetch(`/api/invoices_in/${id}`, { method: 'DELETE' });
      if (!result.ok) return setToastError(true);
      navigate(`/invoices_in`, { replace: true });
    }
  };

  const handleReopenInvoice = async () => {
    setReopenLoading(true);
    const payload = { status: false, bedragopen: invoiceIn.Bedragfaktuur, datumbetalingout: null, memo: invoiceIn.memo, Incasseren: invoiceIn.Incasseren };
    await putRequest(`/api/invoices_in/${invoiceIn.faktuurinvolgnummer}`, payload, setToast, setToastError);
    await fetchData();
    setReopenLoading(false);
  };

  return (
    dataLoaded && (
      <Page
        pagination={pagination}
        title={pageTitle}
        fullWidth
        primaryAction={
          // one-click intake: the first PDF is preselected without marking the form dirty, so the save bar alone needed a detour click
          id !== 'new' && !invoiceIn.processed && invoiceIn.S3ObjectKeys
            ? { content: 'Process invoice', onAction: saveForm, loading: formSubmitting, disabled: formSubmitting }
            : { content: 'New incoming invoice', url: '/invoices_in/new', disabled: !invoiceIn.processed }
        }
        secondaryActions={[
          ...(id !== 'new' && invoiceIn.processed && !invoiceIn.status
            ? [{ content: 'Register payment', onAction: () => setPaymentModalOpen(true), disabled: formSubmitting || isDirty, helpText: isDirty ? 'Save your changes first' : undefined }]
            : []),
          ...(canDeleteProcessedInvoice ? [{ content: 'Delete invoice', destructive: true, onAction: handleDelete, disabled: formSubmitting }] : [])
        ]}
      >
        <TabTitle title={pageTitle} />
        <Toasts toast={toast} setToast={setToast} errorToast={toastError} setErrorToast={setToastError} toastContent="Incoming Invoice saved" />
        <TheSaveBar isDirty={isDirty} savingResult={formSubmitting} handleDiscard={handleDiscard} handleSave={saveForm} />
        {invoiceIn && id !== 'new' && !invoiceIn.processed && !hasSysDoc ? (
          <div style={{ marginBottom: '20px' }}>
            <Banner
              tone="warning"
              title={
                <BlockStack inlineAlign="start">
                  <Text variant="headingMd">This is a new incoming invoice. Please add information on supplier, reference and value to save this invoice.</Text>
                  <InlineStack gap="200">
                    <Text variant="bodyMd" as="h5">
                      Mistakenly uploaded the document for this invoice?
                    </Text>
                    <div style={{ marginTop: '1px' }}>
                      <Button variant="plain" tone="critical" onClick={handleDelete}>
                        Delete invoice
                      </Button>
                    </div>
                  </InlineStack>
                </BlockStack>
              }
            />
          </div>
        ) : null}
        {invoiceIn && invoiceIn.status && (
          <Box paddingBlockEnd="400">
            <Banner title="This invoice is fully paid" status="success" action={(user.role === 1 || user.role === 6) && { content: 'Reopen', onAction: handleReopenInvoice, loading: reopenLoading }} />
          </Box>
        )}
        {id !== 'new' && invoiceIn ? (
          <InvoiceInCollection
            modalOpen={paymentModalOpen}
            setModalOpen={setPaymentModalOpen}
            collectionInvoice={{ ...invoiceIn, supplier_name: company?.NAAM || invoiceIn.Leveranciernaam }}
            fullInvoiceCollection
            fetchData={async () => {
              await fetchData();
              setIntentsRefreshToken((token) => token + 1);
            }}
          />
        ) : null}
        {id !== 'new' && (
          <Box paddingBlockEnd="400">
            <PaymentIntentsCard invoiceId={id} refreshToken={intentsRefreshToken} onChanged={() => fetchData()} />
          </Box>
        )}
        <Layout>
          <Layout.Section variant="oneHalf">
            <Card>
              <Form onSubmit={saveForm} autoComplete="off">
                <FormLayout>
                  <FormLayout.Group>
                    <TextField id="FactuurInNummer" disabled label="Number" value={invoiceIn.FactuurInNummer ? String(invoiceIn.FactuurInNummer) : 'created automatically'} />
                    <TextField
                      className="sentry-ignore"
                      id="Bedragfaktuur"
                      maxLength={60}
                      prefix="€"
                      type="number"
                      autoComplete="off"
                      label={
                        <InlineStack>
                          Total amount <p style={{ color: 'red' }}>*</p>
                        </InlineStack>
                      }
                      value={invoiceIn.Bedragfaktuur ? String(invoiceIn.Bedragfaktuur) : ''}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                    <TextField
                      className="sentry-ignore"
                      id="BedragExclBtw"
                      maxLength={60}
                      prefix="€"
                      type="number"
                      autoComplete="off"
                      label="Amount excl. VAT"
                      helpText={invoiceIn.PeppolJSON ? 'Taken from the Peppol document' : 'Non-Peppol invoices carry no VAT, so this follows the total amount'}
                      value={invoiceIn.BedragExclBtw !== null && invoiceIn.BedragExclBtw !== undefined ? String(invoiceIn.BedragExclBtw) : ''}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <CompanySelect
                      id="LeverancierNummer"
                      label={
                        <InlineStack>
                          Supplier <p style={{ color: 'red' }}>*</p>{' '}
                        </InlineStack>
                      }
                      value={invoiceIn.LeverancierNummer}
                      onChange={(value, id, label) => {
                        setSupplierChangedByUser(true);
                        handleChange(value, id, label);
                      }}
                      isDisabled={formSubmitting}
                    />
                    <TextField
                      className="sentry-ignore"
                      id="bedragopen"
                      maxLength={60}
                      prefix="€"
                      type="number"
                      autoComplete="off"
                      label="Amount open"
                      value={invoiceIn.bedragopen ? String(invoiceIn.bedragopen) : ''}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField
                      id="datumin"
                      maxLength={60}
                      type="date"
                      autoComplete="off"
                      label="Date in"
                      value={String(invoiceIn.datumin).substring(0, 10)}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                    <Select
                      id="ValutaID"
                      autoComplete="off"
                      label="Currency"
                      value={parseInt(invoiceIn.ValutaID) || ''}
                      options={currencies.map((curr) => {
                        return { value: curr.ValutaID, label: curr.Valuta, key: 'CURR-' + curr.ValutaID };
                      })}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField
                      id="datum"
                      maxLength={60}
                      type="date"
                      autoComplete="off"
                      label="Date invoice"
                      value={String(invoiceIn.datum).substring(0, 10)}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                    <IncasserenSelect id="Incasseren" value={invoiceIn.Incasseren} handleChange={handleChange} disabled={formSubmitting} isClearable />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <TextField
                      id="faktuurref"
                      maxLength={50}
                      showCharacterCount
                      autoComplete="off"
                      label={
                        <InlineStack>
                          Reference invoice <p style={{ color: 'red' }}>*</p>
                        </InlineStack>
                      }
                      value={invoiceIn.faktuurref || ''}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                    <TextField
                      id="datumbetaling"
                      maxLength={50}
                      type="date"
                      autoComplete="off"
                      label="Expiry date"
                      value={String(invoiceIn.datumbetaling).substring(0, 10)}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <InlineGrid columns={2}>
                      <BlockStack>
                        <Checkbox id="status" label="Paid" checked={invoiceIn.status} onChange={handleChange} disabled={formSubmitting} />
                        <Checkbox id="Vervallen" label="Expired" checked={invoiceIn.Vervallen} onChange={handleChange} disabled={formSubmitting} />
                      </BlockStack>
                      <OwnerSelect id="GebruikerID" label="Owner" value={invoiceIn.GebruikerID} handleChange={handleChange} disabled={formSubmitting} />
                    </InlineGrid>
                    <TextField
                      clearButton
                      onClearButtonClick={() => handleChange(null, 'datumbetalingout')}
                      id="datumbetalingout"
                      maxLength={50}
                      type="date"
                      label="Payment date"
                      value={String(invoiceIn.datumbetalingout).substring(0, 10)}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                  </FormLayout.Group>
                  <FormLayout.Group>
                    <Select
                      placeholder="Select payment"
                      id="betalingID"
                      label="Payment condition"
                      value={parseInt(invoiceIn.betalingID) || ''}
                      options={paymentconditions.sort((a, b) => alphabetize(a, b, 'betalingNL')).map((cond) => ({ value: cond.betalingID, label: cond.betalingNL, key: 'PC' + cond.betalingID }))}
                      onChange={handleChange}
                      disabled={formSubmitting}
                    />
                    <TextField id="OGM" maxLength={20} showCharacterCount autoComplete="off" label="OGM" value={invoiceIn.OGM || ''} onChange={handleChange} disabled={formSubmitting} />
                  </FormLayout.Group>
                  <TextField id="memo" label="Note" value={invoiceIn.memo || ''} multiline={6} onChange={handleChange} disabled={formSubmitting} />
                </FormLayout>
              </Form>
            </Card>
          </Layout.Section>
          <Layout.Section variant="oneHalf">
            {id !== 'new' && !invoiceIn.processed && invoiceIn.S3ObjectKeys ? (
              <Box paddingBlockEnd="300">
                <PdfCard keys={JSON.parse(invoiceIn.S3ObjectKeys) || []} selectedS3Key={selectedS3Key} setSelectedS3Key={setSelectedS3Key} setIsDirty={setIsDirty} resource={'invoices_in'} />
              </Box>
            ) : null}
            <DocumentCard
              resource="INVOICE_IN"
              id={id}
              title="Incoming invoice document"
              company={company}
              docNumber={invoiceIn.FactuurInNummer}
              isDirty={isDirty}
              generateDocument={false}
              docTypeID={docTypeID}
              docType={docType}
              docLang={company.TaalID}
              documentButtonLoading={formSubmitting}
              setDocumentButtonLoading={setFormSubmitting}
              invoice={invoiceIn}
              setInvoice={setInvoiceIn}
              setIsDirty={setIsDirty}
              setHasSysDoc={setHasSysDoc}
            />
            {id !== 'new' && (
              <Box paddingBlockStart="300">
                <S3DocumentsCard
                  resourceId={invoiceIn.faktuurinvolgnummer}
                  s3ObjectKeys={invoiceIn.S3ObjectKeys}
                  onS3KeysChange={(newKeys) => setInvoiceIn({ ...invoiceIn, S3ObjectKeys: newKeys })}
                  apiBasePath="/api/invoices_in"
                  disabled={formSubmitting}
                  packageName={invoiceIn.FactuurInNummer}
                />
              </Box>
            )}
          </Layout.Section>
          {id !== 'new' && (
            <Layout.Section variant="fullWidth">
              <ApprovalPanel
                key={approvalRefreshKey}
                docType="invoice"
                id={id}
                setToast={setToast}
                setErrorToast={setToastError}
                onChanged={() => {
                  if (!isDirty) fetchData();
                }}
              />
            </Layout.Section>
          )}
          {id !== 'new' && (
            <Layout.Section variant="fullWidth">
              <Card>
                <CardHeading title="Invoice Lines" />
                {invoiceLines.length > 0 ? (
                  <InvoiceLinesTable items={invoiceLines} showFilters={false} showPagination={false} navigate={navigate} />
                ) : (
                  <Box padding="400">
                    <Text variant="bodyMd" as="p">
                      No invoice lines found for this invoice.
                    </Text>
                  </Box>
                )}
              </Card>
            </Layout.Section>
          )}
          <Layout.Section variant="fullWidth">
            <InvoiceBankTransactions type="invoice_in" invoiceId={id} />
          </Layout.Section>
          <Layout.Section variant="fullWidth">
            <EventTimeLine resourceName="INVOICE_IN" resourceId={id} />
          </Layout.Section>
        </Layout>
      </Page>
    )
  );
}
